import { PageBlock } from '@design-system/page-block';
import { Teaser } from '@design-system/teaser';
import { Typography } from '@design-system/typography';
import { BazaarVoiceReviews } from '@phx-husky/bazaarvoice-components';
import React, { FC, lazy } from 'react';

import { ElementOnScreen } from '~/components/ElementOnScreen';
import { useCustomComponents } from '~/context/customComponents';
import { accordionMapper } from '~/mappers/accordion';
import { contactUsBlockMapper } from '~/mappers/contactUsBlock';
import { iframeMapper } from '~/mappers/iframe';
import { Image } from '~/mappers/image/models';
import { imageBlockMapper } from '~/mappers/imageBlock';
import { imagePlusTextBlockMapper } from '~/mappers/imagePlusTextBlock';
import { imageTextBlockMapper } from '~/mappers/imageTextBlock';
import { itemListingMapper } from '~/mappers/itemListing';
import { keyValueMapper } from '~/mappers/keyValue/keyValue';
import { l3ItemListingMapper } from '~/mappers/l3ItemListingMapper';
import { ldsBlockMapper } from '~/mappers/ldsBlock';
import { LoginRegisterFormMapper } from '~/mappers/LoginRegisterForm';
import { mediaPlusTextBlockMapper } from '~/mappers/mediaPlusTextBlock';
import { newsletterMapper } from '~/mappers/newsletter';
import { productComparisonMapper } from '~/mappers/productComparison';
import { promoFormMapper } from '~/mappers/promoForm';
import { promoQuizMapper } from '~/mappers/promoQuiz';
import { retailersBlockMapper } from '~/mappers/retailers';
import { salsifyColumnsTextMapper } from '~/mappers/salsifyColumnsText';
import { salsifyTextMapper } from '~/mappers/salsifyText';
import { searchResultsMapper } from '~/mappers/searchResults';
import { shoppingCartMapper } from '~/mappers/shoppingCart';
import { sitemapMapper } from '~/mappers/sitemap';
import { tabsMapper } from '~/mappers/tabs';
import { teaserMapper } from '~/mappers/teaser';
import { typographyMapper } from '~/mappers/typography';

import { BodyRenderProps } from './models';

const AccordionList = lazy(() => import('~/components/AccordionList'));
const DebugComponent = lazy(
  () => import('~/components/BodyRender/DebugComponent')
);
const ContactUs = lazy(() => import('~/components/ContactUsBlock'));
const Iframe = lazy(() => import('~/components/Iframe'));
const ImagePlusTextBlock = lazy(
  () => import('~/components/ImagePlusTextBlock')
);
const ImageTextBlock = lazy(() => import('~/components/ImageTextBlock'));
const ItemListing = lazy(() => import('~/components/ItemListing'));
const LdsBlock = lazy(() => import('~/components/LdsBlock'));
const LoginRegisterForm = lazy(() => import('~/components/LoginRegisterForm'));
const MediaPlusTextBlock = lazy(
  () => import('~/components/MediaPlusTextBlock')
);
const NewsletterForm = lazy(() => import('~/components/NewsletterForm'));
const ProductComparison = lazy(() => import('~/components/ProductComparison'));
const PromoForm = lazy(() => import('~/components/PromoForm'));
const PromoQuiz = lazy(() => import('~/components/PromoQuiz'));
const RetailersPageBlock = lazy(
  () => import('~/components/RetailersPageBlock')
);
const SalsifyColumnsText = lazy(
  () => import('~/components/SalsifyColumnsText')
);
const SearchResults = lazy(() => import('~/components/SearchResults'));
const ShoppingCart = lazy(() => import('~/components/ShoppingCart'));
const SitemapList = lazy(() => import('~/components/SitemapList'));
const TabsBlock = lazy(() => import('~/components/TabsBlock'));

const ImageBlock = lazy(() => import('~/components/ImageBlock'));

export const typesMapper = {
  default: (typeName) => [
    (item) =>
      process.env.NODE_ENV === 'development' ? (
        <DebugComponent typeName={typeName} {...item} />
      ) : null,
    (item) => item,
  ],
  ItemListing: [ItemListing, itemListingMapper],
  Teaser: [Teaser, teaserMapper],
  ContactUs: [ContactUs, contactUsBlockMapper],
  ImageTextBlock: [
    ImageTextBlock,
    imageTextBlockMapper,
    ({ items, componentId, pageBlockUI }) => ({
      items,
      componentId,
      pageBlockUI,
    }),
  ],
  TextBlock: [Typography, ({ typography }) => typographyMapper(typography)],
  Typography: [Typography, typographyMapper],
  Image: [ImageBlock, (image: Image) => imageBlockMapper({ images: [image] })],
  SalsifyText: [PageBlock, salsifyTextMapper],
  SalsifyColumnsText: [SalsifyColumnsText, salsifyColumnsTextMapper],
  ImageBlock: [ImageBlock, imageBlockMapper],
  ImagePlusTextBlock: [ImagePlusTextBlock, imagePlusTextBlockMapper],
  MediaPlusTextBlock: [MediaPlusTextBlock, mediaPlusTextBlockMapper],
  LdsBlock: [LdsBlock, ldsBlockMapper],
  SitemapBlock: [SitemapList, sitemapMapper],
  Iframe: [Iframe, iframeMapper],
  RetailersBlock: [RetailersPageBlock, retailersBlockMapper],
  SearchResults: [SearchResults, searchResultsMapper],
  Newsletter: [NewsletterForm, newsletterMapper],
  PromoForm: [PromoForm, promoFormMapper],
  PromoQuiz: [PromoQuiz, promoQuizMapper],
  ShoppingCart: [ShoppingCart, shoppingCartMapper],
  BazaarVoiceReviews: [
    BazaarVoiceReviews,
    (items) => items,
    (customValue) => ({
      productId: customValue.productId,
      componentId: customValue.componentId,
    }),
  ],
  Accordion: [AccordionList, accordionMapper],
  LoginRegister: [LoginRegisterForm, LoginRegisterFormMapper],
  L3ItemListing: [ItemListing, l3ItemListingMapper],
  Tabs: [TabsBlock, tabsMapper],
  ProductComparison: [ProductComparison, productComparisonMapper],
};

const BodyRender: FC<BodyRenderProps> = ({
  items,
  currentPageUid,
  externalData,
}) => {
  const customComponents = useCustomComponents();

  const typesMapperWithCustomComponents = {
    ...customComponents.reduce((acc, { key, component }) => {
      acc[key] = [component];

      return acc;
    }, {}),
    ...typesMapper,
  };

  const renderedItems = items.map((item) => {
    const value = Object.values(item).find((val) => val);

    return value || { typeName: 'Unknown component', item: [] };
  });

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {renderedItems?.map((allValues, index) => {
        let { typeName } = allValues;
        const { item } = allValues;

        const originalTypeName = typeName;

        if (['KeyValue', 'CustomContentModel'].includes(typeName)) {
          // eslint-disable-next-line camelcase
          typeName = (item as { component_id: string }[])[0]?.component_id;
        }

        const [ItemComponent, itemMapper, customValue] =
          typesMapperWithCustomComponents[typeName] ||
          typesMapperWithCustomComponents.default(typeName);

        const key = `${typeName}__${index}`;
        let value = Array.isArray(item) ? item[0] : item;

        if (customValue) {
          value = customValue(allValues);
        }

        if (typeName === 'Tabs') {
          value = { ...(value as {}), currentPageUid };
        }

        let itemMapperFn = itemMapper;

        if (originalTypeName === 'KeyValue') {
          itemMapperFn = keyValueMapper;
        }

        return (
          <ElementOnScreen
            key={key}
            componentName={typeName}
            render={(ref) => (
              <ItemComponent
                ref={ref}
                key={key}
                {...(itemMapperFn
                  ? itemMapperFn(value, currentPageUid, externalData)
                  : value)}
              />
            )}
          />
        );
      })}
    </>
  );
};

export default BodyRender;
